import { useLocaleProvider, useTranslate } from "../../context/locale-provider";
import { useWithdrawalFormDetails } from "../../context/withdrawal-details";
import {
  setContractType,
  CONTRACT_TYPES,
} from "../../state/withdrawal-form-details/reducer";
import { Dropdown } from "../../components/dropdown";
import { Button } from "@asosteam/asos-web-component-library-button";
import WithdrawalForm from "../withdrawal-form";
import styles from "./styles";
import { useEffect, useRef, useState } from "react";
import SuccessView from "./success-view";
import { replaceJSX } from "../../utilities/utilities";
import classnames from "classnames";

const LINKS = {
  TCS: "/terms-and-conditions/",
  RETURNS: "/customer-care/returns-refunds/",
  CUSTOMER_CARE: "/customer-care/get-in-touch/",
  PREMIER_TCS: "/payments-and-deliveries/premier-delivery/",
  ASOS_WORLD_PAGE: "/discover/asos-world/",
  ASOS_TCS: "/terms-and-conditions/",
  ASOS_RETURNS: "/customer-care/returns-refunds/",
  ORDERS_HELP_PAGE:
    "/customer-care/order-issues/can-i-amend-or-cancel-my-order/",
  MY_ACCOUNT: "https://my.asos.com/my-account",
  REBOUND: "https://asos.returns.returnsplatform.com/main/step1",
};

const ContractWithdrawalLayout = () => {
  const errorRef = useRef();
  const formRef = useRef();
  const localeProvider = useLocaleProvider();
  const translate = useTranslate(localeProvider);
  const links = { ...LINKS, ...localeProvider?.localisedLinks };
  const [formStatus, setFormStatus] = useState();
  const [contractTypeError, setContractTypeError] = useState(false);
  const [state, dispatch] = useWithdrawalFormDetails();
  const { contractType = "" } = state.withdrawalFormDetails;

  useEffect(() => {
    if (formStatus === "error") {
      errorRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    } else if (formStatus === "success") {
      window.scrollTo(0, 0);
    }
  }, [formStatus]);

  const contractTypeOptions = [
    {
      value: CONTRACT_TYPES.ORDERS,
      label: translate("contract_withdrawal_form_contract_type_orders"),
    },
    {
      value: CONTRACT_TYPES.PREMIER,
      label: translate("contract_withdrawal_form_contract_type_premier"),
    },
    {
      value: CONTRACT_TYPES.LOYALTY,
      label: translate("contract_withdrawal_form_contract_type_loyalty"),
    },
  ];

  const handleContinue = () => {
    if (!contractType) {
      setContractTypeError(true);
    } else {
      setContractTypeError(false);
      formRef?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const link = (href, textKey) => (
    <a key={href} href={href} className={styles.inlineLink}>
      {translate(textKey)}
    </a>
  );

  const renderInfoSection = () => {
    if (contractType === CONTRACT_TYPES.PREMIER) {
      return (
        <div className={styles.card}>
          <p className="app_leavesden2">
            {replaceJSX(
              translate("contract_withdrawal_premier_info_para"),
              "%{tcsLink}",
              link(links.PREMIER_TCS, "contract_withdrawal_link_here")
            )}
          </p>
          <p className="app_leavesden2">
            {translate("contract_withdrawal_premier_form_intro_para_2")}
          </p>
          <p className="app_leavesden2">
            {translate("contract_withdrawal_premier_info_ways_intro")}
          </p>
          <ul className={`${styles.bulletList} app_leavesden2`}>
            <li>{translate("contract_withdrawal_premier_info_way_2")}</li>
            <li>
              {replaceJSX(
                translate("contract_withdrawal_premier_info_way_1"),
                "%{link}",
                link(links.CUSTOMER_CARE, "contract_withdrawal_link_here")
              )}
            </li>
          </ul>
        </div>
      );
    }
    if (contractType === CONTRACT_TYPES.LOYALTY) {
      return (
        <div className={styles.card}>
          <p className="app_leavesden2">
            {replaceJSX(
              replaceJSX(
                translate("contract_withdrawal_loyalty_info_para"),
                "%{pageLink}",
                link(links.ASOS_WORLD_PAGE, "contract_withdrawal_link_here")
              ),
              "%{tcsLink}",
              link(links.ASOS_TCS, "contract_withdrawal_link_here")
            )}
          </p>
          <p className="app_leavesden2">
            {translate("contract_withdrawal_loyalty_info_ways_intro")}
          </p>
          <ul className={`${styles.bulletList} app_leavesden2`}>
            <li>{translate("contract_withdrawal_loyalty_info_way_2")}</li>
            <li>
              {replaceJSX(
                translate("contract_withdrawal_loyalty_info_way_1"),
                "%{link}",
                link(links.CUSTOMER_CARE, "contract_withdrawal_link_here")
              )}
            </li>
          </ul>
        </div>
      );
    }
    if (contractType === CONTRACT_TYPES.ORDERS) {
      return (
        <div className={styles.card}>
          <p className="app_leavesden2">
            {replaceJSX(
              replaceJSX(
                translate("contract_withdrawal_orders_info_para_1"),
                "%{tcsLink}",
                link(links.ASOS_TCS, "contract_withdrawal_link_here")
              ),
              "%{returnsLink}",
              link(links.ASOS_RETURNS, "contract_withdrawal_link_here")
            )}
          </p>
          <p className="app_leavesden2">
            {replaceJSX(
              replaceJSX(
                translate("contract_withdrawal_orders_info_para_2"),
                "%{helpLink}",
                link(links.ORDERS_HELP_PAGE, "contract_withdrawal_link_here")
              ),
              "%{tcsLink}",
              link(links.ASOS_TCS, "contract_withdrawal_link_here")
            )}
          </p>
          <p className="app_leavesden2">
            {translate("contract_withdrawal_orders_info_ways_intro")}
          </p>
          <ul className={`${styles.bulletList} app_leavesden2`}>
            <li>{translate("contract_withdrawal_orders_info_way_1")}</li>
            <li>
              {replaceJSX(
                replaceJSX(
                  translate("contract_withdrawal_orders_info_way_2"),
                  "%{link}",
                  link(links.MY_ACCOUNT, "contract_withdrawal_link_here")
                ),
                "%{link2}",
                link(LINKS.REBOUND, "contract_withdrawal_link_here")
              )}
            </li>
            {links.WITHDRAWAL_FORM_PDF && (
              <li>
                {replaceJSX(
                  translate("contract_withdrawal_orders_info_way_3"),
                  "%{link}",
                  link(
                    links.WITHDRAWAL_FORM_PDF,
                    "contract_withdrawal_link_here"
                  )
                )}
              </li>
            )}
            <li>
              {replaceJSX(
                translate("contract_withdrawal_orders_info_way_4"),
                "%{link}",
                link(links.CUSTOMER_CARE, "contract_withdrawal_link_here")
              )}
            </li>
          </ul>
        </div>
      );
    }
    return null;
  };

  const formSectionTitleKey = {
    [CONTRACT_TYPES.PREMIER]: "contract_withdrawal_premier_form_title",
    [CONTRACT_TYPES.LOYALTY]: "contract_withdrawal_loyalty_form_title",
    [CONTRACT_TYPES.ORDERS]: "contract_withdrawal_orders_form_title",
  }[contractType];

  const formSectionIntroKey = {
    [CONTRACT_TYPES.PREMIER]: "contract_withdrawal_premier_form_intro",
    [CONTRACT_TYPES.LOYALTY]: "contract_withdrawal_loyalty_form_intro",
    [CONTRACT_TYPES.ORDERS]: "contract_withdrawal_orders_form_intro",
  }[contractType];

  const renderFormSection = () => (
    <>
      <div
        id="withdrawal-form"
        className={classnames(styles.card, styles.title)}
      >
        <h2 className="app_barnsley4">{translate(formSectionTitleKey)}</h2>
      </div>
      <div className={styles.card}>
        <p className="app_leavesden2">{translate(formSectionIntroKey)}</p>
      </div>
      <div className={styles.card} ref={formRef}>
        {formStatus === "error" && (
          <div className={styles.errorResponse} ref={errorRef}>
            <div>
              <h3 className="app_london2">
                {translate("contract_withdrawal_form_error_title")}
              </h3>
              <p className="app_leavesden2">
                {translate("contract_withdrawal_form_error_description")}
              </p>
            </div>
          </div>
        )}
        <WithdrawalForm setFormStatus={setFormStatus} />
      </div>
    </>
  );

  return (
    <div className={styles.container}>
      {formStatus === "success" ? (
        <SuccessView />
      ) : (
        <>
          <div className={classnames(styles.card, styles.title)}>
            <h1 className="app_barnsley4">
              {translate("contract_withdrawal_form_title")}
            </h1>
          </div>
          <div className={styles.card}>
            <p className="app_leavesden2">
              {translate("contract_withdrawal_form_intro_para_1")}
            </p>
            <p className="app_leavesden2">
              {translate("contract_withdrawal_form_intro_para_2")}
            </p>
            <p className="app_leavesden2">
              {replaceJSX(
                translate("contract_withdrawal_form_intro_para_3"),
                "%{tcsLink}",
                link(links.TCS, "contract_withdrawal_link_here")
              )}
            </p>
            <p className="app_leavesden2">
              {replaceJSX(
                replaceJSX(
                  translate("contract_withdrawal_form_intro_para_4"),
                  "%{tcsLink}",
                  link(links.TCS, "contract_withdrawal_link_here")
                ),
                "%{returnsLink}",
                link(links.RETURNS, "contract_withdrawal_link_here")
              )}
            </p>
            <p className="app_leavesden2">
              {translate("contract_withdrawal_form_intro_para_5")}
            </p>
          </div>

          <div className={classnames(styles.card, styles.formGroup)}>
            <div>
              <p className="app_leavesden2">
                {translate("contract_withdrawal_form_contract_selector_label")}
              </p>
              <span className={`${styles.requiredLabel} app_leavesden2`}>
                {translate("contract_withdrawal_form_required_information")}
              </span>
              <div className={styles.contractSelectorControl}>
                <label
                  htmlFor="contractType"
                  className={classnames(
                    "app_london3",
                    styles.contractSelectorLabel
                  )}
                >
                  {translate("contract_withdrawal_form_contract_type_label")}
                </label>
                <Dropdown
                  id="contractType"
                  ariaLabel={translate(
                    "contract_withdrawal_form_contract_type_label"
                  )}
                  options={contractTypeOptions}
                  value={contractType}
                  placeholder={translate(
                    "contract_withdrawal_form_contract_type_placeholder"
                  )}
                  onChange={(e) => {
                    dispatch(setContractType(e.target.value));
                    setContractTypeError(!e.target.value);
                  }}
                  hasError={contractTypeError}
                  errorMessage={translate(
                    "contract_withdrawal_form_contract_type_error"
                  )}
                  required
                />
              </div>
              <Button className="app_button" onClick={handleContinue}>
                {translate("contract_withdrawal_form_continue_cta")}
              </Button>
            </div>
          </div>

          {contractType && renderInfoSection()}

          {contractType && renderFormSection()}
        </>
      )}
    </div>
  );
};

export default ContractWithdrawalLayout;
